import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Button, Group, Select, Stack, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, benchmarkApi, costsApi, referenceApi, type CostPlan } from '../../api';
import { PRICE_INDEX_TYPES, formatQuarter } from '../../format';
import { LocationField } from '../../core/LocationField';
import { FACTOR_NAME, INDEX_NOW, latestIndex, matchDistrict } from './conversion';

const num = (v: number, digits = 3): string => v.toLocaleString('de-DE', { maximumFractionDigits: digits, useGrouping: false });

/**
 * Where and when (AP-80): the project's location suggests a regional factor,
 * the chosen building type the price index. Both live as named values of the
 * plan (Regionalfaktor, BPI_aktuell), so every converted line follows a change.
 */
export function RegionalPanel({ projectId, plan, canEdit }: { projectId: string; plan: CostPlan; canEdit: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const allowed = useQuery({ queryKey: ['benchmarks', 'me'], queryFn: benchmarkApi.me });
  const factors = useQuery({ queryKey: ['regional-factors'], queryFn: referenceApi.factors, enabled: allowed.data?.allowed === true });
  const indices = useQuery({ queryKey: ['price-indices'], queryFn: referenceApi.indices });
  const projects = useQuery({ queryKey: ['projects'], queryFn: api.projects });
  const location = projects.data?.find((p) => p.id === projectId)?.location ?? null;
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['costs', projectId] });
  const valueOf = (name: string) => plan.values.find((v) => v.name.toLowerCase() === name.toLowerCase());
  const setValue = useMutation({
    mutationFn: async ({ name, formula, label }: { name: string; formula: string; label: string }) => {
      const existing = valueOf(name);
      if (existing) await costsApi.updateValue(projectId, existing.id, { formula, label });
      else await costsApi.addValue(projectId, { name, formula, label, kind: 'rate' });
    },
    onSettled: refresh,
  });
  const setType = useMutation({ mutationFn: (priceIndexType: string) => costsApi.updatePlan(projectId, { priceIndexType }), onSettled: refresh });

  const list = factors.data ?? [];
  const { match, candidates } = matchDistrict(location, list);
  const current = valueOf(FACTOR_NAME);
  const nowIndex = latestIndex(indices.data ?? [], plan.priceIndexType);
  const planNow = valueOf(INDEX_NOW);
  const labelOf = (f: (typeof list)[number]): string => `${f.district} (${[f.source, f.edition].filter(Boolean).join(' ')})`;
  // The district the plan's factor came from: same label and figure. The
  // field used to be fixed to "empty" and jumped back after every choice.
  const stored = current ? list.find((f) => current.label === labelOf(f) && current.formula === num(f.factor))
    ?? list.find((f) => current.label?.startsWith(`${f.district} (`) && current.formula === num(f.factor)) : undefined;
  const [picked, setPicked] = useState<string | null>(null);
  const shown = setValue.isPending && picked ? picked : stored?.id ?? null;
  const chooseFactor = (id: string | null): void => {
    const f = list.find((x) => x.id === id);
    if (!f) return;
    setPicked(f.id);
    setValue.mutate({ name: FACTOR_NAME, formula: num(f.factor), label: labelOf(f) }, { onSettled: () => setPicked(null) });
  };
  const error = setValue.error ?? setType.error;

  return (
    <Stack gap={6}>
      <Group align="flex-end" gap="md" wrap="wrap">
        <div style={{ width: 300 }} title="Dasselbe Feld wie in den Projektdaten – eine Änderung gilt an beiden Stellen.">
          <LocationField projectId={projectId} suggestions={list.map((f) => f.district)} />
        </div>
        {allowed.data?.allowed && (
          <Select
            label="Regionalfaktor"
            description={current ? `${FACTOR_NAME} = ${current.formula}${current.label ? ` · ${current.label}` : ''}` : 'noch keiner im Kostenplan'}
            placeholder={list.length ? 'Kreis wählen' : 'keine Regionalfaktoren erfasst'}
            data={list.map((f) => ({ value: f.id, label: `${f.district} – ${num(f.factor)}${f.area !== 'Deutschland' ? ` (${f.area})` : ''}` }))}
            value={shown}
            onChange={(id) => id && id !== stored?.id && chooseFactor(id)}
            allowDeselect={false}
            nothingFoundMessage="kein Kreis gefunden"
            searchable
            disabled={!canEdit || list.length === 0}
            w={320}
            comboboxProps={{ withinPortal: true }}
          />
        )}
        <Select
          label="Baupreisindex für"
          description={nowIndex ? `neuester Wert: ${formatQuarter(nowIndex.period)} = ${num(nowIndex.value, 2)}` : 'noch keine Indexwerte erfasst'}
          data={[...new Set([...PRICE_INDEX_TYPES, ...(indices.data ?? []).filter((i) => i.work === 'insgesamt' && i.vat === 'ohne').map((i) => i.buildingType)])].filter((t) => t !== 'Außenanlagen')}
          value={plan.priceIndexType}
          onChange={(v) => v && v !== plan.priceIndexType && setType.mutate(v)}
          allowDeselect={false}
          disabled={!canEdit}
          w={240}
          comboboxProps={{ withinPortal: true }}
        />
      </Group>
      {canEdit && match && current?.formula !== num(match.factor) && (
        <Group gap="xs">
          <Text size="xs">Zum Standort passt {match.district} mit {num(match.factor)}.</Text>
          <Button size="compact-xs" variant="light" loading={setValue.isPending} onClick={() => chooseFactor(match.id)}>
            {current ? 'Regionalfaktor ersetzen' : 'als Regionalfaktor übernehmen'}
          </Button>
        </Group>
      )}
      {canEdit && !match && candidates.length > 1 && (
        <Text size="xs" c="dimmed">Zum Standort passen mehrere Kreise ({candidates.slice(0, 4).map((c) => c.district).join(', ')}{candidates.length > 4 ? ' …' : ''}) – bitte oben wählen.</Text>
      )}
      {canEdit && nowIndex && planNow && planNow.formula !== num(nowIndex.value, 2) && (
        <Group gap="xs">
          <Text size="xs">{INDEX_NOW} = {planNow.formula}; neuer ist {formatQuarter(nowIndex.period)} mit {num(nowIndex.value, 2)}.</Text>
          <Button size="compact-xs" variant="light" loading={setValue.isPending}
            onClick={() => setValue.mutate({ name: INDEX_NOW, formula: num(nowIndex.value, 2), label: `Baupreisindex ${nowIndex.buildingType}, ${formatQuarter(nowIndex.period)}${nowIndex.base ? ` (${nowIndex.base})` : ''}, ohne USt` })}>
            {INDEX_NOW} aktualisieren
          </Button>
        </Group>
      )}
      {error && <Alert color="red" py={4}>{error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'}</Alert>}
    </Stack>
  );
}
